import { Injectable, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeStorageKey = 'ff-theme-preference';
  private readonly selectedThemeSignal = signal<ThemePreference>('light');

  readonly selectedTheme = this.selectedThemeSignal.asReadonly();

  initializeFromStorage(): void {
    const storedTheme = this.readStoredThemePreference();
    this.selectedThemeSignal.set(storedTheme);
    this.applyTheme(storedTheme);
  }

  toggleTheme(): void {
    const nextTheme: ThemePreference = this.selectedThemeSignal() === 'dark' ? 'light' : 'dark';
    this.setTheme(nextTheme);
  }

  setTheme(theme: ThemePreference): void {
    this.selectedThemeSignal.set(theme);
    this.persistThemePreference(theme);
    this.applyTheme(theme);
  }

  private applyTheme(theme: ThemePreference): void {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
  }

  private readStoredThemePreference(): ThemePreference {
    try {
      return localStorage.getItem(this.themeStorageKey) === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }

  private persistThemePreference(theme: ThemePreference): void {
    try {
      localStorage.setItem(this.themeStorageKey, theme);
    } catch {
      // Ignore storage errors and still apply the selected theme for this session.
    }
  }
}

